import clsx from 'clsx'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {find, size} from 'lodash'
import {DEFAULT_PAGE, DEFAULT_PAGE_SIZE, FORMAT_DATE} from '../../../../../gori/constants'
import {convertUserTimeZone} from '../../../../../gori/helpers'
import {useCancelToken} from '../../../../../gori/hooks'
import {useAuth, useDisabled} from '../../../../../gori/providers'
import {TableSkeleton} from '../../../../../gori/components'
import {
  Button,
  PageSize,
  PaginateModel,
  PaginateWidget,
  Table,
  TableBody,
  ValidationErrorModal,
} from '../../../../../gori/widgets'
import {A1BatchService, A1_CONSTANTS} from '../../../a1'
import {A1RemovedOrder} from '../../definitions'

type Props = {
  show: boolean
  batchId: any
  handleClose: () => void
}

const RemovedOrdersHistoryModal: React.FC<Props> = ({show, batchId, handleClose}) => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const {setPageDisabled} = useDisabled()
  const {newCancelToken, isCancel} = useCancelToken()
  const [dataRemoved, setDataRemoved] = useState<{
    data: A1RemovedOrder[]
    pagination: PaginateModel | null
  }>({
    data: [],
    pagination: null,
  })
  const [page, setPage] = useState<number>(Number(DEFAULT_PAGE))
  const [pageSize, setPageSize] = useState<number>(Number(DEFAULT_PAGE_SIZE))
  const [loading, setLoading] = useState<boolean>(false)
  const [validationErrors, setValidationErrors] = useState<any>()

  const columns = useMemo(() => {
    return [
      {
        id: 'bae_id',
        Header: intl.formatMessage({id: 'BAE_ID'}),
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap text-gray-800 fw-bolder',
        Cell: ({row}: {row: any}) => row.original?.bae_id || '-',
      },
      {
        id: 'tracking_number',
        Header: intl.formatMessage({id: 'TRACKING_NUMBER'}),
        headerClassName: 'min-w-200px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => row.original?.tracking_number || '-',
      },
      {
        id: 'removed_at',
        Header: intl.formatMessage({id: 'REMOVED_AT'}),
        headerClassName: 'min-w-120px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) =>
          row.original?.removed_at
            ? convertUserTimeZone(row.original?.removed_at, currentUser, FORMAT_DATE.DATE)
            : '-',
      },
      {
        id: 'removed_by',
        Header: intl.formatMessage({id: 'REMOVED_BY'}),
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => row.original?.removed_by || '-',
      },
      {
        id: 'customs_status',
        Header: intl.formatMessage({id: 'CUSTOM_STATUS'}),
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap text-gray-800',
        Cell: ({row}: {row: any}) => {
          const foundRelease: any =
            find(Object.values(A1_CONSTANTS.OPTIONS_RELEASE_STATUS), {
              value: row.original?.customs_status,
            }) || null

          return foundRelease ? intl.formatMessage({id: foundRelease?.label}) : '-'
        },
      },
      {
        id: 'current_batch',
        Header: intl.formatMessage({id: 'CURRENT_BATCH'}),
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap',
        Cell: ({row}: {row: any}) => {
          if (row.original?.is_transferred_to_another_origin_partner) {
            return (
              <span className='text-muted'>
                {intl.formatMessage({id: 'ORDER_TRANSFERRED_TO_ANOTHER_ORIGIN_PARTNER'})}
              </span>
            )
          }

          return row.original?.current_batch && row.original?.current_batch_id ? (
            <a
              href={`/a1-batches/${row.original?.current_batch_id}`}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary fw-bolder text-decoration-underline'
            >
              {row.original?.current_batch}
            </a>
          ) : (
            '-'
          )
        },
      },
    ]
  }, [currentUser, intl])

  const getRemovedOrders = useCallback(async () => {
    try {
      setLoading(true)
      setPageDisabled(true)
      const config = {
        params: {
          page,
          page_size: pageSize,
        },
        cancelToken: newCancelToken(),
      }
      const response: any = await A1BatchService.getRemovedOrders(batchId, config)
      setDataRemoved({
        data: response?.removed_orders || [],
        pagination: response?.pagination || null,
      })
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setLoading(false)
      setPageDisabled(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, page, pageSize, newCancelToken, isCancel])

  useEffect(() => {
    if (show && batchId) {
      getRemovedOrders()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, batchId, getRemovedOrders])

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (option: any) => {
    setPage(1)
    setPageSize(option.value)
  }

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => setValidationErrors(undefined)}
          response={validationErrors}
        />
      )}
      <Modal
        id='gori_modal_removed_orders_history'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='mw-1100px h-auto'
        show={show}
        backdrop='static'
        onHide={handleClose}
      >
        <Modal.Header closeButton>
          <Modal.Title bsPrefix={'fw-bolder fs-1'}>
            {intl.formatMessage({id: 'REMOVED_ORDERS_HISTORY'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className='removed-orders-scroll'>
              <TableBody className='px-0' height='auto' minHeight='auto'>
                <TableSkeleton countColumn={6} countRow={pageSize} />
              </TableBody>
            </div>
          ) : (
            <>
              <div
                className={clsx('removed-orders-scroll', {
                  'removed-orders-scroll--empty': size(dataRemoved.data) < 1,
                })}
              >
                <TableBody className='px-0' height='auto' minHeight='auto'>
                  <Table
                    columns={columns}
                    data={dataRemoved.data}
                    theadClass='text-gray-600 fw-bold border-bottom-2'
                    tbodyClass='text-gray-600 fw-bold'
                    useCheckBox={false}
                    useSort={false}
                    usePagination={false}
                  />
                  {size(dataRemoved.data) < 1 && (
                    <div className='d-flex justify-content-center text-muted fs-5 py-10'>
                      {intl.formatMessage({id: 'NO_ORDERS_HAVE_BEEN_REMOVED_FROM_THIS_BATCH'})}
                    </div>
                  )}
                </TableBody>
              </div>
              {dataRemoved.pagination && size(dataRemoved.data) > 0 && (
                <div className='d-flex align-items-center justify-content-between mt-4'>
                  <div className='d-flex align-items-center'>
                    <PageSize event={handlePageSizeChange} pageSize={pageSize} />
                    <div className='ms-4 text-muted'>
                      {intl.formatMessage(
                        {id: 'SHOWING_TOTAL_ROWS'},
                        {current: size(dataRemoved.data), total: dataRemoved.pagination.total}
                      )}
                    </div>
                  </div>
                  <PaginateWidget
                    onPageChange={handlePageChange}
                    pagination={dataRemoved.pagination}
                  />
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end'>
            <Button
              className='btn btn-secondary'
              label={intl.formatMessage({id: 'CLOSE'})}
              event={handleClose}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {RemovedOrdersHistoryModal}
